import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuth } from '../../contexts/AuthContext';
import { userApi } from '../../services/api';
import { profileSchema } from '../../utils/validationSchemas';
import LoadingSpinner from '../LoadingSpinner';
import './ProfileForm.css';

const ProfileForm = () => {
  const { user, updateUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
      age: user?.age || '',
      gender: user?.gender || '',
    },
    mode: 'onBlur'
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      // Only send changed fields
      const updateData = {};
      if (data.name !== user.name) updateData.name = data.name;
      if (data.age !== user.age) updateData.age = data.age || null;
      if (data.gender !== user.gender) updateData.gender = data.gender || null;

      if (Object.keys(updateData).length === 0) {
        setMessage({ type: 'info', text: 'No changes to save.' });
        return;
      }

      const response = await userApi.updateProfile(updateData);
      updateUser(response.data.user);
      setMessage({ type: 'success', text: 'Account updated.' });
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update account'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="profile-form-container">
      <div className="profile-header">
        <h2>Your details</h2>
        <p>Update your personal information</p>
      </div>

      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="profile-form">
        <div className="form-group">
          <label htmlFor="name">Full name *</label>
          <input
            id="name"
            type="text"
            {...register('name')}
            className={errors.name ? 'input-error' : ''}
            placeholder="Enter your full name"
          />
          {errors.name && (
            <span className="field-error">{errors.name.message}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="age">Age</label>
            <input
              id="age"
              type="number"
              {...register('age')}
              className={errors.age ? 'input-error' : ''}
              placeholder="Age"
              min="1"
              max="150"
            />
            {errors.age && (
              <span className="field-error">{errors.age.message}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="gender">Gender</label>
            <select
              id="gender"
              {...register('gender')}
              className={errors.gender ? 'input-error' : ''}
            >
              <option value="">Select gender</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not_to_say">Prefer not to say</option>
            </select>
            {errors.gender && (
              <span className="field-error">{errors.gender.message}</span>
            )}
          </div>
        </div>

        <div className="form-group readonly">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            type="email"
            value={user?.email || ''}
            readOnly
            className="readonly-input"
          />
          <small className="form-note">
            Email cannot be changed. Contact support if you need to update this.
          </small>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="save-button"
            disabled={!isDirty || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoadingSpinner size="small" />
                Saving...
              </>
            ) : (
              'Save changes'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;
